import { useRef } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import DuckIcon from "./DuckIcon";
import UniversalSearch from "./UniversalSearch";
import NotificationBell from "./NotificationBell";
import SoftphoneCallBar from "./comms/SoftphoneCallBar";
import BottomNav from "./home/BottomNav";
import CommissionAddendumGate from "./CommissionAddendumGate";
import UpdateAvailableBanner from "./UpdateAvailableBanner";
import BooksJobSync from "./BooksJobSync";
import { useVisualViewportPin } from "../lib/useVisualViewportPin";
import { useUiTracking, logNavClick } from "../lib/uiEvents";

/** Page titles for sub-pages — shown in header on mobile for orientation */
const PAGE_TITLES: Record<string, string> = {
  "/dispatch": "Dispatch Board",
  "/drone": "Drone Inspection Board",
  "/qc": "QC Board",
  "/finance": "Finance",
  "/my-pipeline": "My Pipeline",
  "/outreach": "Outreach",
  "/notifications": "Notifications",
  "/crew": "Crew",
  "/team-boards": "Team Boards",
  "/phone-health": "Phone Health",
  "/admin": "Admin",
};

export default function Layout() {
  const { member, signOut } = useAuth();
  const location = useLocation();

  // Usage telemetry — logs page_view + time-to-first-action on every route.
  // Fire-and-forget; never blocks the UI. Read-gated to admin/sales_director.
  useUiTracking(member);
  // Pin the staff portal header to the visual viewport. Pure transform —
  // when the page isn't zoomed/panned, no change applies and the header
  // scrolls normally with the page (existing behavior; preserves sub-page
  // sticky-top-0 elements). When iOS Safari pinch-zooms (despite our
  // viewport meta lock), the hook counter-translates + inverse-scales the
  // header so it stays visible at the top of the visible viewport at
  // original pixel size. May 11, 2026.
  const headerRef = useRef<HTMLElement>(null);
  useVisualViewportPin(headerRef);

  // Check if we're on a sub-page (not home), including /deal/:id
  const isHome = location.pathname === "/" || location.pathname === "/staff";
  const isDealPage = location.pathname.startsWith("/deal/");
  const isSubPage = !isHome;
  const pageTitle = isDealPage ? "" : PAGE_TITLES[location.pathname] ?? "";

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* May 11, 2026: header gets a safe-area-inset-top pad so the dark bar
          extends behind the iOS notch / Dynamic Island. Combined with the
          maximum-scale=1 viewport (index.html), the header can no longer be
          pinch-zoomed off-screen (the failure mode in the original report).
          NOT made `sticky` because multiple sub-pages (CanvasAppt header,
          table theads, Canvas page's `fixed inset-0` map) already use
          top-of-viewport positioning and would collide with a sticky parent
          header. */}
      {/* Desktop (sm+): header + global nav stick to the top while scrolling
          long boards. Mobile keeps the scroll-away header + fixed bottom bar,
          so it doesn't collide with mobile sub-page sticky theads. */}
      <div className="sm:sticky sm:top-0 sm:z-40">
      <header
        ref={headerRef}
        className="bg-bardin-ink text-white"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          {/* On sub-pages: show a back-to-home arrow (mobile-friendly, always visible).
              On home: show the full logo. */}
          {isSubPage ? (
            <Link to="/" className="flex items-center gap-2 shrink-0 group">
              <svg className="w-5 h-5 text-slate-300 group-hover:text-white transition" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              <DuckIcon size={24} color="#ff9e7a" />
              {pageTitle && (
                <span className="text-sm font-medium text-slate-200 hidden sm:inline">{pageTitle}</span>
              )}
            </Link>
          ) : (
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <DuckIcon size={28} color="#ff9e7a" />
              <span className="font-semibold tracking-tight hidden sm:inline">Bardin Roofing</span>
              <span className="ml-1 text-xs text-slate-300 hidden md:inline">Staff Portal</span>
            </Link>
          )}
          <UniversalSearch />
          <div className="flex items-center gap-2 sm:gap-3 text-sm shrink-0">
            {/* Crew App jump — persistent so managers can hop to /crew
                from any staff page. Orange chip so it reads as a distinct
                destination rather than another nav item. */}
            <Link
              to="/crew"
              onClick={() => logNavClick("crew")}
              title="Crew Leaderboard"
              className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-bardin-orange/90 hover:bg-bardin-orange text-white text-xs font-semibold uppercase tracking-wider transition"
            >
              <svg
                className="w-3.5 h-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.4}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 9H4a2 2 0 0 1-2-2V5h4M18 9h2a2 2 0 0 0 2-2V5h-4M18 2H6v7a6 6 0 0 0 12 0V2ZM12 17v4M8 21h8" />
              </svg>
              Crew
            </Link>
            {/* Mobile-only: icon-only crew jump to save header real estate */}
            <Link
              to="/crew"
              onClick={() => logNavClick("crew")}
              title="Crew Leaderboard"
              aria-label="Crew Leaderboard"
              className="sm:hidden inline-flex items-center justify-center w-8 h-8 rounded-full bg-bardin-orange/90 hover:bg-bardin-orange text-white"
            >
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.4}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 9H4a2 2 0 0 1-2-2V5h4M18 9h2a2 2 0 0 0 2-2V5h-4M18 2H6v7a6 6 0 0 0 12 0V2ZM12 17v4M8 21h8" />
              </svg>
            </Link>
            {/* Canvas jump — persistent so anyone can hop to /canvas
                from any staff page. Slate chip distinguishes it from the
                orange Crew chip. Mirrors the Crew button's dual desktop /
                mobile pattern. */}
            <Link
              to="/canvas"
              onClick={() => logNavClick("canvas")}
              title="Canvas — knock-pin map + leaderboard"
              className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-bardin-teal/90 hover:bg-bardin-teal text-white text-xs font-semibold uppercase tracking-wider transition"
            >
              <svg
                className="w-3.5 h-3.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.4}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0Z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              Canvas
            </Link>
            {/* Mobile-only: icon-only canvas jump to save header real estate */}
            <Link
              to="/canvas"
              onClick={() => logNavClick("canvas")}
              title="Canvas — knock-pin map + leaderboard"
              aria-label="Canvas"
              className="sm:hidden inline-flex items-center justify-center w-8 h-8 rounded-full bg-bardin-teal/90 hover:bg-bardin-teal text-white"
            >
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.4}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0Z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </Link>
            <NotificationBell />
            {member && (
              <span className="text-slate-200 hidden lg:inline">
                {member.full_name}
                <span className="ml-2 px-2 py-0.5 rounded bg-bardin-teal text-xs">
                  {member.role.replace(/_/g, " ")}
                </span>
              </span>
            )}
            {/* Oct 29, 2026: icon-only on phones. The full "Sign out" label
                pushed the header row past a 390px screen and clipped it. */}
            <button
              onClick={signOut}
              title="Sign out"
              aria-label="Sign out"
              className="inline-flex items-center justify-center w-8 h-8 sm:w-auto sm:h-auto sm:px-3 sm:py-1.5 rounded bg-slate-700 hover:bg-slate-600 text-sm"
            >
              <svg
                className="w-4 h-4 sm:hidden"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
              </svg>
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      {/* Global navigation — desktop top tab row (under the header) + mobile
          fixed bottom bar. Self-hides on the full-screen Canvas tools. */}
      <BottomNav />
      </div>

      <main className="flex-1 pb-16 sm:pb-0">
        <Outlet />
      </main>

      {/* Finance-tier only, renders nothing: keeps the Jobs page in Books fed with each built job's figures. */}
      <BooksJobSync />

      {/* Global softphone call bar — only renders for users in SOFTPHONE_ALLOWED_USERS
          and only when a call is active. Hidden otherwise. */}
      <SoftphoneCallBar />

      {/* Sep 15, 2026 — "Portal updated, reload" prompt. A tab left open
          across a deploy keeps running the old bundle forever (SPA); this
          polls index.html for a new main-bundle hash and offers a Reload.
          Hidden during a live softphone call. */}
      <UpdateAvailableBanner />

      {/* Jul 20, 2026 commission-structure addendum — RM/TL/ASM must authorize.
          Appears Monday morning; hard-blocks the CRM from 10:00 AM ET until
          the member's team_acknowledgments row exists. Renders null for
          everyone else and after authorization. */}
      <CommissionAddendumGate />
    </div>
  );
}
